import * as THREE from 'three';
import { RomanticCoupleAvatars } from './characterAvatars.js';

export class ValentineScene3D {
  constructor(container) {
    this.container = container;
    this.width = container.clientWidth || window.innerWidth;
    this.height = container.clientHeight || window.innerHeight;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(38, this.width / this.height, 0.1, 100);
    this.camera.position.set(0, 1.8, 8.5);

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance'
    });
    this.renderer.setSize(this.width, this.height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;

    this.container.appendChild(this.renderer.domElement);

    // Mouse Interaction
    this.mouse = { x: 0, y: 0, targetX: 0, targetY: 0 };
    this.clock = new THREE.Clock();

    // Scene Elements
    this.setupLighting();
    this.setupPedestal();
    this.setupParticles();

    // Couple Avatars
    this.couple = new RomanticCoupleAvatars();
    this.scene.add(this.couple.root);

    // Listeners
    window.addEventListener('resize', this.onResize.bind(this));
    window.addEventListener('mousemove', this.onMouseMove.bind(this));

    this.scrollProgress = 0;
    this.animate = this.animate.bind(this);
    requestAnimationFrame(this.animate);
  }

  setupLighting() {
    // Soft Romantic Ambient Glow
    const ambientLight = new THREE.AmbientLight(0xFFF0E8, 1.2);
    this.scene.add(ambientLight);

    // Key Spotlight from Top Right
    const mainSpot = new THREE.DirectionalLight(0xFFE8DD, 2.2);
    mainSpot.position.set(4, 7, 5);
    mainSpot.castShadow = true;
    mainSpot.shadow.mapSize.width = 1024;
    mainSpot.shadow.mapSize.height = 1024;
    mainSpot.shadow.camera.near = 0.5;
    mainSpot.shadow.camera.far = 20;
    mainSpot.shadow.bias = -0.001;
    this.scene.add(mainSpot);

    // Fill Light from Left (Soft Blush Pink)
    const fillLight = new THREE.DirectionalLight(0xFFD6E0, 1.0);
    fillLight.position.set(-5, 4, 3);
    this.scene.add(fillLight);

    // Subtle Rim Light from Behind
    const rimLight = new THREE.DirectionalLight(0xFFFFFF, 1.4);
    rimLight.position.set(0, 5, -5);
    this.scene.add(rimLight);
  }

  setupPedestal() {
    this.pedestalGroup = new THREE.Group();
    this.pedestalGroup.position.set(0, 0.25, 0);

    // Marble Floating Top Cylinder
    const cylinderGeo = new THREE.CylinderGeometry(2.2, 2.3, 0.35, 48);
    const marbleMat = new THREE.MeshStandardMaterial({
      color: 0xFAF6F0,
      roughness: 0.35,
      metalness: 0.05
    });
    const cylinder = new THREE.Mesh(cylinderGeo, marbleMat);
    cylinder.position.y = -0.18;
    cylinder.receiveShadow = true;
    this.pedestalGroup.add(cylinder);

    // Rose Gold Rim Ring
    const goldRingGeo = new THREE.TorusGeometry(2.22, 0.035, 16, 64);
    const goldMat = new THREE.MeshStandardMaterial({
      color: 0xE5B869,
      roughness: 0.2,
      metalness: 0.85
    });
    const goldRing = new THREE.Mesh(goldRingGeo, goldMat);
    goldRing.rotation.x = Math.PI / 2;
    goldRing.position.y = -0.01;
    this.pedestalGroup.add(goldRing);

    // Soft Ambient Contact Shadow Disc
    const shadowGeo = new THREE.CircleGeometry(2.8, 32);
    const shadowMat = new THREE.MeshBasicMaterial({
      color: 0xE2C8CB,
      transparent: true,
      opacity: 0.35
    });
    const shadowPlane = new THREE.Mesh(shadowGeo, shadowMat);
    shadowPlane.rotation.x = -Math.PI / 2;
    shadowPlane.position.y = -0.38;
    this.pedestalGroup.add(shadowPlane);

    this.scene.add(this.pedestalGroup);
  }

  setupParticles() {
    this.particleCount = 55;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(this.particleCount * 3);
    const scales = new Float32Array(this.particleCount);
    const rotSpeed = new Float32Array(this.particleCount);

    for (let i = 0; i < this.particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 12;
      positions[i * 3 + 1] = Math.random() * 8 - 2;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 8;
      scales[i] = Math.random() * 0.15 + 0.05;
      rotSpeed[i] = Math.random() * 0.02 - 0.01;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    // Romantic Petal Mesh Elements
    this.petalMeshes = [];
    const petalShape = new THREE.SphereGeometry(0.12, 8, 6);
    petalShape.scale(1.2, 0.25, 0.8);

    const petalColors = [0xFF8FA3, 0xFF4D6D, 0xFFCCD5, 0xFDC5A7];

    for (let i = 0; i < this.particleCount; i++) {
      const color = petalColors[Math.floor(Math.random() * petalColors.length)];
      const petalMat = new THREE.MeshStandardMaterial({
        color: color,
        roughness: 0.6,
        transparent: true,
        opacity: 0.85
      });
      const petal = new THREE.Mesh(petalShape, petalMat);
      petal.position.set(
        positions[i * 3],
        positions[i * 3 + 1],
        positions[i * 3 + 2]
      );
      petal.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI
      );
      petal.userData = {
        speedY: Math.random() * 0.008 + 0.004,
        rotX: Math.random() * 0.02 - 0.01,
        rotY: Math.random() * 0.02 - 0.01,
        rotZ: Math.random() * 0.02 - 0.01,
        initialX: petal.position.x
      };
      this.scene.add(petal);
      this.petalMeshes.push(petal);
    }
  }

  onResize() {
    this.width = this.container.clientWidth || window.innerWidth;
    this.height = this.container.clientHeight || window.innerHeight;
    this.camera.aspect = this.width / this.height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.width, this.height);
  }

  onMouseMove(e) {
    const x = (e.clientX / window.innerWidth) * 2 - 1;
    const y = -(e.clientY / window.innerHeight) * 2 + 1;
    this.mouse.targetX = x * 0.5;
    this.mouse.targetY = y * 0.3;
  }

  /**
   * Update Scroll Progress & Drive Pinned GSAP Avatar Reveal
   * @param {number} p Scroll progress from 0.0 to 1.0
   */
  setScrollProgress(p) {
    this.scrollProgress = p;

    // Phase 1 (0 to 0.15): Hero text visible, avatars hidden under pedestal horizon
    // Phase 2 (0.15 to 0.55): Avatars smoothly emerge upwards from bottom/center
    // Phase 3 (0.55 to 1.0): Close up emotional camera framing and celebratory perspective
    if (p <= 0.1) {
      // Hidden beneath pedestal
      const subP = p / 0.1;
      this.couple.root.position.y = -5.0 + subP * 0.5;
      this.couple.root.scale.set(0.01, 0.01, 0.01);
      this.camera.position.set(0, 2.2, 9.5);
    } else if (p <= 0.6) {
      // Ascending onto pedestal & revealing emotional presence
      const revealProgress = (p - 0.1) / 0.5; // 0 -> 1
      const easeOut = 1 - Math.pow(1 - revealProgress, 3);

      this.couple.root.position.y = -4.5 + easeOut * 4.75; // -4.5 up to 0.25
      const s = Math.min(1.0, easeOut * 1.05);
      this.couple.root.scale.set(s, s, s);

      // Camera transitions smoothly closer while keeping entire pedestal in view
      this.camera.position.y = 2.2 - easeOut * 0.4;
      this.camera.position.z = 9.5 - easeOut * 2.5;
    } else {
      // Fully revealed and active
      this.couple.root.position.y = 0.25;
      this.couple.root.scale.set(1.0, 1.0, 1.0);

      const lateProgress = (p - 0.6) / 0.4;
      this.camera.position.y = 1.8 + Math.sin(lateProgress * Math.PI) * 0.12;
      this.camera.position.z = 7.0 - lateProgress * 0.3;
    }
  }

  triggerCelebration() {
    this.couple.setEmotion('celebrate');
    let burstCount = 0;
    const interval = setInterval(() => {
      if (burstCount++ > 20) clearInterval(interval);
    }, 100);
  }

  animate() {
    requestAnimationFrame(this.animate);
    const delta = this.clock.getDelta();

    // Smooth Mouse Damping
    this.mouse.x += (this.mouse.targetX - this.mouse.x) * 0.06;
    this.mouse.y += (this.mouse.targetY - this.mouse.y) * 0.06;

    // Apply Subtle Camera Sway
    this.camera.position.x = this.mouse.x * 0.8;
    this.camera.lookAt(0, 1.25 + this.mouse.y * 0.2, 0);

    // Update Avatars Animation
    this.couple.update(delta, this.scrollProgress, this.mouse.x, this.mouse.y);

    // Update Floating Rose Petals
    for (let i = 0; i < this.petalMeshes.length; i++) {
      const petal = this.petalMeshes[i];
      petal.position.y -= petal.userData.speedY;
      petal.rotation.x += petal.userData.rotX;
      petal.rotation.y += petal.userData.rotY;
      petal.rotation.z += petal.userData.rotZ;

      // Subtle horizontal breeze drift
      petal.position.x = petal.userData.initialX + Math.sin(this.clock.elapsedTime + i) * 0.4;

      // Recycle petals back to top
      if (petal.position.y < -3.0) {
        petal.position.y = 5.0;
      }
    }

    // Pedestal gentle idle sheen rotation
    if (this.pedestalGroup) {
      this.pedestalGroup.rotation.y = Math.sin(this.clock.elapsedTime * 0.3) * 0.05;
    }

    this.renderer.render(this.scene, this.camera);
  }
}
